import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, UserPlus, KeyRound, Save } from "lucide-react";

const Section = ({ title, children, testid }) => (
  <div className="border border-white/10 bg-[#111827] p-6" data-testid={testid}>
    <h3 className="font-heading text-base font-semibold text-slate-100 mb-4">{title}</h3>
    {children}
  </div>
);

export default function AdminAccounts({ user, onUserUpdate }) {
  const [admins, setAdmins] = useState([]);
  const [account, setAccount] = useState({ name: user.name || "", email: user.email || "" });
  const [pwd, setPwd] = useState({ current_password: "", new_password: "" });
  const [newAdmin, setNewAdmin] = useState({ email: "", name: "", password: "" });
  const [resetFor, setResetFor] = useState(null); // user_id
  const [resetPwd, setResetPwd] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/users").then((r) => setAdmins(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const saveAccount = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/auth/account", account);
      toast.success("Compte mis à jour");
      onUserUpdate?.({ ...user, name: data.name, email: data.email });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/change-password", pwd);
      toast.success("Mot de passe modifié");
      setPwd({ current_password: "", new_password: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const createAdmin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/users", newAdmin);
      toast.success("Administrateur créé");
      setNewAdmin({ email: "", name: "", password: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const removeAdmin = async (id) => {
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("Administrateur supprimé");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const submitReset = async (id) => {
    try {
      await api.put(`/admin/users/${id}/password`, { new_password: resetPwd });
      toast.success("Mot de passe réinitialisé");
      setResetFor(null);
      setResetPwd("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-accounts">
      <div className="grid gap-6 md:grid-cols-2">
        <Section title="Mon compte" testid="account-section">
          <form onSubmit={saveAccount} className="space-y-3" data-testid="account-form">
            <input data-testid="account-name-input" className="input-dark" placeholder="Nom" value={account.name} onChange={(e) => setAccount({ ...account, name: e.target.value })} required />
            <input data-testid="account-email-input" className="input-dark" type="email" placeholder="Email (identifiant)" value={account.email} onChange={(e) => setAccount({ ...account, email: e.target.value })} required />
            <button data-testid="account-save-btn" disabled={busy} className="btn-primary inline-flex items-center gap-2 disabled:opacity-60">
              <Save size={14} /> Enregistrer
            </button>
          </form>
        </Section>

        <Section title="Changer mon mot de passe" testid="password-section">
          <form onSubmit={savePassword} className="space-y-3" data-testid="password-form">
            <input data-testid="current-password-input" className="input-dark" type="password" placeholder="Mot de passe actuel" value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} required />
            <input data-testid="new-password-input" className="input-dark" type="password" placeholder="Nouveau mot de passe (min. 8)" minLength={8} value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} required />
            <button data-testid="password-save-btn" disabled={busy} className="btn-primary inline-flex items-center gap-2 disabled:opacity-60">
              <KeyRound size={14} /> Modifier
            </button>
          </form>
        </Section>
      </div>

      <Section title="Ajouter un administrateur" testid="create-admin-section">
        <form onSubmit={createAdmin} className="grid gap-3 sm:grid-cols-3" data-testid="create-admin-form">
          <input data-testid="new-admin-name" className="input-dark" placeholder="Nom" value={newAdmin.name} onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })} required />
          <input data-testid="new-admin-email" className="input-dark" type="email" placeholder="Email" value={newAdmin.email} onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })} required />
          <input data-testid="new-admin-password" className="input-dark" type="password" placeholder="Mot de passe (min. 8)" minLength={8} value={newAdmin.password} onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })} required />
          <button data-testid="create-admin-btn" disabled={busy} className="btn-primary inline-flex items-center gap-2 disabled:opacity-60 sm:col-span-3 justify-self-start">
            <UserPlus size={14} /> Créer le compte
          </button>
        </form>
      </Section>

      <Section title={`Administrateurs (${admins.length})`} testid="admins-list-section">
        <div className="space-y-3">
          {admins.map((a) => (
            <div key={a.user_id} className="border border-white/10 bg-[#0d1322] p-4" data-testid={`admin-row-${a.user_id}`}>
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-slate-200 font-medium">
                    {a.name} {a.user_id === user.user_id && <span className="text-[10px] text-teal-400 ml-1">(vous)</span>}
                  </p>
                  <p className="text-xs text-slate-500">{a.email}</p>
                </div>
                <div className="flex items-center gap-3">
                  <button data-testid={`admin-reset-${a.user_id}`} onClick={() => { setResetFor(resetFor === a.user_id ? null : a.user_id); setResetPwd(""); }} className="text-xs text-slate-400 hover:text-amber-300 inline-flex items-center gap-1">
                    <KeyRound size={13} /> Réinitialiser
                  </button>
                  {a.user_id !== user.user_id && (
                    <button data-testid={`admin-delete-${a.user_id}`} onClick={() => removeAdmin(a.user_id)} className="text-slate-600 hover:text-rose-400 transition-colors">
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              </div>
              {resetFor === a.user_id && (
                <div className="mt-3 flex items-center gap-2" data-testid={`admin-reset-form-${a.user_id}`}>
                  <input data-testid={`admin-reset-input-${a.user_id}`} className="input-dark flex-1" type="password" placeholder="Nouveau mot de passe (min. 8)" minLength={8} value={resetPwd} onChange={(e) => setResetPwd(e.target.value)} />
                  <button data-testid={`admin-reset-submit-${a.user_id}`} onClick={() => submitReset(a.user_id)} className="btn-primary text-xs">Valider</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
